import React from "react";

const VISUALS = {
    brandOrigin: {
        src: "/assets/generated/zild-origin-coffee-table.webp",
        alt: "Mesa de trabalho com xícara de café, caderno e marca circular deixada pela conversa.",
    },
    operationsBlueprint: {
        src: "/assets/illustrations/operations-1280.webp",
        alt: "Ilustração de equipes de atendimento, vendas e planejamento conectadas.",
    },
    secureLedger: {
        src: "/assets/illustrations/governance-1280.webp",
        alt: "Ilustração de revisão humana e ambiente de dados com acesso controlado.",
    },
    pricingCredits: {
        src: "/assets/illustrations/expansion-1280.webp",
        alt: "Ilustração da expansão gradual da adoção de IA entre equipes.",
    },
    fieldMemos: {
        src: "/assets/illustrations/knowledge-1280.webp",
        alt: "Ilustração de biblioteca e equipe compartilhando conhecimento.",
    },
};

export const generatedVisualUrl = (asset) => {
    const visual = VISUALS[asset] || VISUALS.operationsBlueprint;
    return visual.src;
};

export const GeneratedSurface = ({
    asset,
    caption,
    className = "",
    children,
    as: Component = "div",
    priority = false,
    style,
    ...props
}) => {
    const visual = VISUALS[asset] || VISUALS.operationsBlueprint;

    return (
        <Component
            className={`zild-generated-surface ${className}`.trim()}
            style={{
                "--zild-generated-bg": `url(${visual.src})`,
                ...style,
            }}
            {...props}
        >
            {priority ? (
                <img
                    className="zild-generated-surface__preload"
                    src={visual.src}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    decoding="async"
                />
            ) : null}
            {children}
            <style>{`
                .zild-generated-surface {
                    position: relative;
                    overflow: hidden;
                    isolation: isolate;
                }

                .zild-generated-surface::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    z-index: 0;
                    background-image: var(--zild-generated-bg);
                    background-size: cover;
                    background-position: var(--zild-generated-position, center);
                    opacity: var(--zild-generated-opacity, 0.2);
                    filter: saturate(0.88) contrast(1.04);
                    transform: scale(1.02);
                    pointer-events: none;
                }

                .zild-generated-surface::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                    background: var(
                        --zild-generated-overlay,
                        linear-gradient(135deg, rgba(245, 248, 248, 0.84), rgba(245, 248, 248, 0.7))
                    );
                    pointer-events: none;
                }

                .zild-generated-surface > :not(style):not(.zild-generated-surface__preload) {
                    position: relative;
                    z-index: 2;
                }

                .zild-generated-surface__preload {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    opacity: 0;
                    pointer-events: none;
                }

                .zild-generated-surface__caption {
                    display: inline-flex;
                    width: max-content;
                    max-width: calc(100% - 36px);
                    margin-top: 18px;
                    padding: 8px 10px;
                    border-radius: var(--zild-radius-md);
                    background: rgba(16, 43, 48, 0.78);
                    color: rgba(255, 255, 255, 0.86);
                    font-family: var(--zild-font-mono);
                    font-size: 10px;
                    line-height: 1.35;
                    letter-spacing: 0.07em;
                    text-transform: uppercase;
                }
            `}</style>
        </Component>
    );
};

const GeneratedVisual = ({ asset, caption, className = "", priority = false }) => {
    const visual = VISUALS[asset] || VISUALS.operationsBlueprint;

    return (
        <figure className={`zild-generated-visual ${className}`.trim()}>
            <img
                src={visual.src}
                alt={visual.alt}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
            />
            {caption ? <figcaption>{caption}</figcaption> : null}
            <style>{`
                .zild-generated-visual {
                    position: relative;
                    min-width: 0;
                    margin: 0;
                    overflow: hidden;
                    border-radius: var(--zild-radius-lg);
                    border: 1px solid var(--zild-border);
                    background: var(--zild-table-paper);
                    box-shadow: var(--zild-shadow-panel);
                    isolation: isolate;
                }

                .zild-generated-visual::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background:
                        linear-gradient(180deg, rgba(16, 43, 48, 0), rgba(16, 43, 48, 0.1)),
                        radial-gradient(circle at 18% 12%, rgba(0, 194, 209, 0.12), transparent 34%);
                    pointer-events: none;
                    z-index: 1;
                }

                .zild-generated-visual img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    min-height: 240px;
                    object-fit: cover;
                    transform: scale(1.001);
                }

                .zild-generated-visual figcaption {
                    position: absolute;
                    left: 18px;
                    bottom: 18px;
                    z-index: 2;
                    max-width: calc(100% - 36px);
                    padding: 9px 10px;
                    border-radius: var(--zild-radius-md);
                    background: rgba(16, 43, 48, 0.82);
                    color: rgba(255, 255, 255, 0.88);
                    font-family: var(--zild-font-mono);
                    font-size: 10px;
                    line-height: 1.35;
                    letter-spacing: 0.07em;
                    text-transform: uppercase;
                }

                @media (max-width: 640px) {
                    .zild-generated-visual img {
                        min-height: 220px;
                    }
                }
            `}</style>
        </figure>
    );
};

export default GeneratedVisual;
